/** Remuneration Review rules (docs/PERFORMANCE-REMUNERATION.md, section 6). All amounts are AUD. */
export const BASES = ['Compliance', 'Below-band', 'Performance', 'Retention', 'General'];
export const REM_DEFAULTS = { ceilingPct: 10, superPct: 12, probationMonths: 6, fullTimeHours: 38 };
/** R-2a: cumulative CPI index for a year; the base year (and earlier) is 1. */
export function cpiIndex(moves, year, baseYear) {
    return moves.filter((m) => m.year > baseYear && m.year <= year).reduce((i, m) => i * (1 + m.movePct / 100), 1);
}
const to100 = (n) => Math.round(n / 100) * 100;
/** R-2b: band for a year = base × index, rounded to $100. "Indexation moves bands, not pay." */
export function indexedBand(band, moves, year) {
    const index = cpiIndex(moves, year, band.baseYear);
    const min = to100(band.baseMin * index);
    const max = to100(band.baseMax * index);
    return { min, max, mid: (min + max) / 2, index };
}
/** R-3: full-time-equivalent annual pay. Casuals: base hourly rate (ex loading) × 38 × 52. */
export function fteEquivalent(p, fullTimeHours = 38) {
    if (p.payRate == null)
        return null;
    if (p.payBasis === 'hour')
        return Math.round(p.payRate * fullTimeHours * 52);
    const fte = p.fte && p.fte > 0 ? p.fte : 1;
    return Math.round(p.payRate / fte);
}
/** Current annual pay (for the pool). Casuals: hourly × usual hours × 52 (hours per week, else FTE × 38). */
export function currentAnnualPay(p, fullTimeHours = 38) {
    if (p.payRate == null)
        return null;
    if (p.payBasis === 'hour')
        return Math.round(p.payRate * (p.hoursPerWeek ?? (p.fte ?? 0) * fullTimeHours) * 52);
    return Math.round(p.payRate);
}
/** R-4: position in band, 0 = minimum, 1 = maximum. */
export const bandPosition = (fteEq, band) => (band.max === band.min ? 0 : (fteEq - band.min) / (band.max - band.min));
export function bandLabel(pos) {
    if (pos < 0)
        return 'Below band';
    if (pos <= 0.25)
        return 'Entry';
    if (pos <= 0.6)
        return 'Competent';
    if (pos <= 0.85)
        return 'Advanced';
    if (pos <= 1)
        return 'Expert';
    return 'Above band';
}
/** R-5: eligibility for a discretionary increase. */
export function eligibility(p, effectiveDate, s = REM_DEFAULTS) {
    const reasons = [];
    if (p.finalRating == null)
        reasons.push('No final rating');
    else if (p.finalRating <= 2)
        reasons.push(`Final rating ${p.finalRating}`);
    if (p.startDate) {
        const [y, m, d] = p.startDate.split('-').map(Number);
        const end = new Date(Date.UTC(y, m - 1 + s.probationMonths, d)).toISOString().slice(0, 10);
        if (end > effectiveDate)
            reasons.push(`In probation until ${end}`);
    }
    return { eligible: reasons.length === 0, reasons };
}
/** R-8: guardrails. Blocking issues stop approval; warnings don't. */
export function guardrails(rows, poolAmount, s = REM_DEFAULTS) {
    const issues = [];
    let allocated = 0;
    for (const r of rows) {
        allocated += r.current * (r.pct / 100);
        const newFte = r.fteEq * (1 + r.pct / 100);
        if (r.pct > s.ceilingPct)
            issues.push({ who: r.name, sev: 'bad', text: `Increase above the ${s.ceilingPct}% individual ceiling` });
        if (newFte > r.band.max && r.pct > 0 && !r.outOfBand)
            issues.push({ who: r.name, sev: 'bad', text: 'New rate exceeds band maximum — needs a Director out-of-band decision' });
        if (!r.eligible && r.pct > 0 && r.basis !== 'Compliance')
            issues.push({ who: r.name, sev: 'bad', text: `Not eligible for a discretionary increase (${r.reasons.join(', ')})` });
        if (r.pct === 0 && !r.note?.trim())
            issues.push({ who: r.name, sev: 'warn', text: 'Nil increase needs a recorded reason' });
        if (newFte < r.band.min && !r.outOfBand)
            issues.push({ who: r.name, sev: 'warn', text: 'Still below the band minimum after this increase' });
    }
    const remaining = poolAmount - allocated;
    if (remaining < -0.5)
        issues.unshift({ who: 'Pool', sev: 'bad', text: `Allocation exceeds the approved pool by ${aud(-remaining)}` });
    return { issues, allocated, remaining, blocking: issues.some((i) => i.sev === 'bad') };
}
export const aud = (n) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
/** Default effective date: 1 July of the review year ("the first full pay period on or after 1 July" — the Director confirms the exact date). */
export const defaultEffectiveDate = (year) => `${year}-07-01`;
//# sourceMappingURL=remuneration.js.map